import { Box, Switch, Typography } from "@mui/material";
import React, { Dispatch, SetStateAction, useState } from "react";
import InfoPopper from "../../../../components/popper/InfoPopper";
import {
  ChartExpandAndTableButton,
  displayType,
} from "../../../../components/chart/ChartExpandAndTableButton";
import { useTranslation } from "react-i18next";
import { common } from "../../../../i18n";
import { GoalItem } from "../../../../models/GoalSettingDashboard";
import {
  GridColDef,
  GridColumnGroupingModel,
} from "@mui/x-data-grid";
import { GradientBarChart } from "./GradientBarChart";
import ChartCheckbox from "../../../../components/chart/ChartCheckbox";
import { tableStyle } from "./GoalDashboardTableStyle";
import { TrendLine } from "../../../../components/icon/TrendLine";
import { AppDataGrid } from "../../../../components/datagrid/AppDataGrid";
import dayjs from "dayjs";

interface Props {
  goal: GoalItem;
  showNonPlanned: boolean;
  setShowNonPlanned: Dispatch<SetStateAction<boolean>>;
  rows: any[];
  columns: GridColDef[];
  columnGroupingModel?: GridColumnGroupingModel;
}

export const CustomGoalSettingChart = ({
  goal,
  showNonPlanned,
  setShowNonPlanned,
  rows,
  columns,
  columnGroupingModel,
}: Props) => {
  const { t } = useTranslation([common], { nsMode: "fallback" });
  const [type, setType] = useState<displayType>("chart");

  // For chartExpand
  const yearRange: number[] = [
    goal.baseYear,
    ...Array.from(
      { length: goal.targetYear - goal.startYear + 1 },
      (_, i) => goal.startYear + i,
    ),
  ];
  // ✅ xLabels
  const xLabels = yearRange.map(String);
  const getPlanValue = (year: number, key: string) => {
    const plan = goal.plans.find((p) => p.year === year);
    if (!plan) return null;
    const value = plan[key as keyof typeof plan];
    return value == null ? null : value; // null แปลว่า "ไม่มีข้อมูล"
  };
  // ✅ series แยกตาม methodology for chartexpand
  const series =
    goal.methodology === "baseline"
      ? [
          {
            type: "line",
            label: "BAU-Emission",
            data: yearRange.map((y) => getPlanValue(y, "bauEmission")),
            color: "#FF4747",
          },
          {
            type: "line",
            label: "Target-Emission",
            data: yearRange.map((y) => getPlanValue(y, "targetEmission")),
            color: "#0EA552",
          },
          {
            type: "bar",
            label: "Scope-1",
            data: yearRange.map((y) => getPlanValue(y, "scope1")),
            color: "#2563EB",
            stack: "emission",
          },
          {
            type: "bar",
            label: "Scope-2",
            data: yearRange.map((y) => getPlanValue(y, "scope2")),
            color: "#EC4899",
            stack: "emission",
          },
          {
            type: "bar",
            label: "Scope-3",
            data: yearRange.map((y) => getPlanValue(y, "scope3")),
            color: "#F59E0B",
            stack: "emission",
          },
          {
            type: "line",
            label: "Variance-Reduction",
            data: yearRange.map((y) => {
              const total = getPlanValue(y, "total");
              const bau = getPlanValue(y, "bauEmission");

              if (total != null && total !== 0) {
                return bau;
              }
              return total;
            }),
            color: "transparent",
            area: true,
            showMark: false,
          },
        ]
      : [
          {
            type: "line",
            label: "Target-Emission",
            data: yearRange.map((y) => getPlanValue(y, "targetEmission")),
            color: "#0EA552",
          },
          {
            type: "bar",
            label: "Scope-1",
            data: yearRange.map((y) => getPlanValue(y, "scope1")),
            color: "#2563EB",
            stack: "emission",
          },
          {
            type: "bar",
            label: "Scope-2",
            data: yearRange.map((y) => getPlanValue(y, "scope2")),
            color: "#EC4899",
            stack: "emission",
          },
          {
            type: "bar",
            label: "Scope-3",
            data: yearRange.map((y) => getPlanValue(y, "scope3")),
            color: "#F59E0B",
            stack: "emission",
          },
          {
            type: "line",
            label: "Variance-Reduction",
            data: yearRange.map((y) => {
              const total = getPlanValue(y, "total");
              const baseEmission = getPlanValue(y, "baseYearEmission");

              if (total != null && total !== 0) {
                return baseEmission;
              }
              return total;
            }),
            color: "transparent",
            area: true,
            showMark: false,
          },
        ];

  const baseYearEmission =
    goal.methodology === "baseline"
      ? getPlanValue(goal.baseYear, "bauEmission")
      : getPlanValue(goal.baseYear, "baseYearEmission");

  const [filter, setFilter] = useState<string[]>([
    ...series.map((e) => e.label),
    "Base-Year-Reference", // ✅ เพิ่มให้ติ๊กเปิดตั้งแต่เริ่ม
  ]);

  const [filterChart, setFilterChart] = useState<string[]>([
    ...series.map((e) => e.label),
    "Base-Year-Reference", // ✅ เพิ่มให้ติ๊กเปิดตั้งแต่เริ่ม
  ]);

  const toggleFilter = (label: string) => {
    setFilter((prev) =>
      prev.includes(label) ? prev.filter((f) => f !== label) : [...prev, label],
    );
  };

  const toggleFilterChart = (label: string) => {
    setFilterChart((prev) =>
      prev.includes(label) ? prev.filter((f) => f !== label) : [...prev, label],
    );
  };

  const filteredRowsForChart = showNonPlanned
    ? rows // แสดงทั้งหมด
    : rows.filter((r) => {
        const y = Number(r.year);
        return (
          y === goal.baseYear || (y >= goal.startYear && y <= goal.targetYear)
        );
      });

  // ✅ ดึงปีจาก rows หลังกรอง
  const yearRangeChart: number[] = Array.from(
    new Set(
      filteredRowsForChart
        .map((r) => r.year)
        .filter((y: number | null | undefined): y is number => y != null),
    ),
  ).sort((a, b) => a - b);

  const xLabelsChart = yearRangeChart.map(String);

  // ✅ series สำหรับ chart mode (ใช้เฉพาะปีที่กรองแล้ว) for chart normal
  const seriesChart =
    goal.methodology === "baseline"
      ? [
          {
            type: "line",
            label: "BAU-Emission",
            data: yearRangeChart.map((y) => getPlanValue(y, "bauEmission")),
            color: "#FF4747",
          },
          {
            type: "line",
            label: "Target-Emission",
            data: yearRangeChart.map((y) => getPlanValue(y, "targetEmission")),
            color: "#0EA552",
          },
          {
            type: "bar",
            label: "Scope-1",
            data: yearRangeChart.map((y) => getPlanValue(y, "scope1")),
            color: "#2563EB",
            stack: "emission",
          },
          {
            type: "bar",
            label: "Scope-2",
            data: yearRangeChart.map((y) => getPlanValue(y, "scope2")),
            color: "#EC4899",
            stack: "emission",
          },
          {
            type: "bar",
            label: "Scope-3",
            data: yearRangeChart.map((y) => getPlanValue(y, "scope3")),
            color: "#F59E0B",
            stack: "emission",
          },
          // gradient area
          {
            type: "line",
            label: "Variance-Reduction",
            data: yearRangeChart.map((y) => {
              const total = getPlanValue(y, "total");
              const bau = getPlanValue(y, "bauEmission");

              if (total != null && total !== 0) {
                return bau;
              }
              return total;
            }),
            color: "transparent",
            area: true,
            showMark: false,
            connectNulls: false,
          },
        ]
      : [
          {
            type: "line",
            label: "Target-Emission",
            data: yearRangeChart.map((y) => getPlanValue(y, "targetEmission")),
            color: "#0EA552",
          },
          {
            type: "bar",
            label: "Scope-1",
            data: yearRangeChart.map((y) => getPlanValue(y, "scope1")),
            color: "#2563EB",
            stack: "emission",
          },
          {
            type: "bar",
            label: "Scope-2",
            data: yearRangeChart.map((y) => getPlanValue(y, "scope2")),
            color: "#EC4899",
            stack: "emission",
          },
          {
            type: "bar",
            label: "Scope-3",
            data: yearRangeChart.map((y) => getPlanValue(y, "scope3")),
            color: "#F59E0B",
            stack: "emission",
          },
          // gradient area
          {
            type: "line",
            label: "Variance-Reduction",
            data: yearRangeChart.map((y) => {
              const total = getPlanValue(y, "total");
              const baseEmission = getPlanValue(y, "baseYearEmission");

              if (total != null && total !== 0) {
                return baseEmission;
              }
              return total;
            }),
            color: "transparent",
            area: true,
            showMark: false,
          },
        ];

  return (
    <Box display={"flex"} flexDirection={"column"} gap={"20px"}>
      <Box
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        gap={"8px"}
      >
        <Typography variant="h3" flexGrow={1} color={"var(--dark)"}>
          {goal.name}
          <span style={{ marginLeft: 8, fontSize: "14px", fontWeight: 400 }}>
            {t("Goal-Dashboard-Description", {
              param1: `${
                goal.scope === "scope123" ? t("Scope123") : t("Scope12")
              }`,
              param2: `${goal.startYear}`,
              param3: `${goal.baseYear}`,
              param4: `${
                goal.methodology === "baseYear"
                  ? t("Base-Year")
                  : t("Base-Line")
              }`,
            })}
          </span>

          <Box>
            <Typography color={"var(--gray1)"}>
              {t("Last-Update")}:{" "}
              {goal.updatedAt
                ? dayjs(goal.updatedAt).format("DD/MM/YYYY")
                : "-"}
            </Typography>
          </Box>
        </Typography>

        <Box display={"flex"} flexDirection={"row"} m={"0px 12px"} gap={"8px"}>
          <Switch
            size="small"
            checked={showNonPlanned}
            onChange={() => setShowNonPlanned((prev) => !prev)}
            disabled={type === "chartexp"}
          />
          <Typography color={"var(--dark)"}>{t("Non-Planned-Year")}</Typography>
        </Box>
        <ChartExpandAndTableButton setType={setType} type={type} />
      </Box>
      <Box
        display={"flex"}
        justifyContent={"flex-end"}
        gap={"24px"}
        flexWrap={"wrap"}
        style={{ width: "100%" }}
      >
        {type === "table" ? (
          <>
            <AppDataGrid
              rows={rows}
              columns={columns}
              columnGroupingModel={columnGroupingModel}
              pagination
              pageSizeOptions={[10, 20, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              sx={{
                ...tableStyle("dark"),
                "& .base-year-row": {
                  backgroundColor: "#DEDEDE !important",
                },
              }}
              getRowClassName={(params) =>
                params.row.year === goal.baseYear ? "base-year-row" : ""
              }
            />
          </>
        ) : type === "chart" ? (
          <>
            {seriesChart.map((e) => (
              <ChartCheckbox
                key={e.label}
                text={`${t(e.label)}`}
                color={e.label === "Variance-Reduction" ? "#93D2CA" : e.color}
                checked={filterChart.includes(e.label)}
                onChange={(_, checked) => toggleFilterChart(e.label)}
                icon={
                  e.label === "Target-Emission" ? (
                    <TrendLine color="primary" />
                  ) : e.label === "BAU-Emission" ? (
                    <TrendLine color="error" />
                  ) : undefined
                }
              />
            ))}
            {/* <ChartCheckbox
              key={`Base-Year-Reference`}
              text={"Variance-Reduction"}
              color={"#0B9485"}
              checked={filterChart.includes("Base-Year-Reference")}
              onChange={(_, checked) =>
                toggleFilterChart("Base-Year-Reference")
              }
            /> */}
            <GradientBarChart
              xLabels={xLabelsChart}
              series={seriesChart.filter((s) =>
                filterChart.includes(s.label ?? ""),
              )}
              methodology={goal.methodology}
              baseYearEmission={baseYearEmission}
              filter={filterChart}
              goalItem={goal}
              type="chart"
            />
          </>
        ) : (
          <>
            {series.map((e) => (
              <ChartCheckbox
                key={e.label}
                text={e.label}
                color={e.label === "Variance-Reduction" ? "#93D2CA" : e.color}
                checked={filter.includes(e.label)}
                onChange={(_, checked) => toggleFilter(e.label)}
                icon={
                  e.label === "Target-Emission" ? (
                    <TrendLine color="primary" />
                  ) : e.label === "BAU-Emission" ? (
                    <TrendLine color="error" />
                  ) : undefined
                }
              />
            ))}
            <GradientBarChart
              xLabels={xLabels}
              series={series.filter((s) => filter.includes(s.label ?? ""))}
              methodology={goal.methodology}
              baseYearEmission={baseYearEmission}
              filter={filter}
              goalItem={goal}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default CustomGoalSettingChart;
